import Login from "@/pages/Login/Login";
import Dashboard from "@/pages/Dashboard/Dashboard";
import Members from "@/pages/Members/Members";
import MemberDetails from "@/pages/MemberDetails/MemberDetails";
import Waitlist from "@/pages/Waitlist/Waitlist";
import ApplicationReview from "@/pages/ApplicationReview/ApplicationReview";
import Gatherings from "@/pages/Gatherings/Gatherings";
import GatheringDetails from "@/pages/GatheringDetails/GatheringDetails";
import CreateGathering from "@/pages/CreateGathering/CreateGathering";
import QuestionnaireManagement from "@/pages/Questionnaire-Management/QuestionnaireManagement";
import Ticketing from "@/pages/Ticketing/Ticketing";
import CitiesChapters from "@/pages/CitiesChapters/CitiesChapters";
import Subscriptions from "@/pages/Subscriptions/Subscriptions";
import PaymentIcon from "@/components/SVGComponents/PaymentIcon";
import Payments from "@/pages/Payments/Payments";
import AuditLogIcon from "@/components/SVGComponents/AuditLogIcon";
import TeamsIcon from "@/components/SVGComponents/TeamsIcon";
import PastEvents from "@/pages/PastEvents/PastEvents";
import Support from "@/pages/Support/Support";
import AuditLog from "@/pages/AuditLog/AuditLog";
import TeamRoles from "@/pages/TeamRoles/TeamRoles";
import Settings from "@/pages/Settings/Settings";
import GridIcon from "@/components/SVGComponents/GridIcon";
import QuestionnaireIcon from "@/components/SVGComponents/QuestionnaireIcon";
import UsersIcon from "@/components/SVGComponents/UsersIcon";
import SubcriptionIcon from "@/components/SVGComponents/SubscriptionIcon"
import WaitListIcon from "@/components/SVGComponents/WaitListIcon";
import CalendarIcon from "@/components/SVGComponents/CalendarIcon";
import TicketIcon from "@/components/SVGComponents/TicketIcon";
import QrScanIcon from "@/components/SVGComponents/QrScanIcon";
import DoorCheckIn from "@/pages/DoorCheckIn/DoorCheckIn";
import PastEventIcon from "@/components/SVGComponents/PastEventIcon";
import LocationPinIcon from "@/components/SVGComponents/LocationPinIcon";
import PlaceholderNavIcon from "@/components/SVGComponents/PlaceholderNavIcon";
import ClockIcon from "@/components/SVGComponents/ClockIcon";
import ShieldIcon from "@/components/SVGComponents/ShieldIcon";
import SettingsIcon from "@/components/SVGComponents/SettingsIcon";
import { captalizeEachWord } from "@/lib/utils";
import type { RouteConfig } from "@/types/routestypes";

// Named keys (not Record<string, RouteConfig>) so `routes.publicRoutes.LOGIN`
// etc. are known-present, not "possibly undefined" — see
// docs/tooling-and-build.md on noUncheckedIndexedAccess.
interface PublicRoutes {
  LOGIN: RouteConfig;
  RESET_PASSWORD: RouteConfig;
}

interface PrivateRoutes {
  DASHBOARD: RouteConfig;
  MEMBERS: RouteConfig;
  MEMBER_DETAILS: RouteConfig;
  WAITLIST: RouteConfig;
  WAITLIST_APPLICATION_REVIEW: RouteConfig;
  QUESTIONNAIRES: RouteConfig;
  GATHERINGS: RouteConfig;
  GATHERINGS_CREATE: RouteConfig;
  GATHERINGS_EDIT: RouteConfig;
  GATHERING_DETAILS: RouteConfig;
  TICKETING: RouteConfig;
  DOOR_CHECK_IN: RouteConfig;
  CITIES_CHAPTERS: RouteConfig;
  SUBSCRIPTIONS: RouteConfig;
  PAYMENTS: RouteConfig;
  PAST_EVENTS: RouteConfig;
  SUPPORT: RouteConfig;
  AUDIT_LOG: RouteConfig;
  TEAM_ROLES: RouteConfig;
  SETTINGS: RouteConfig;
}

// Grown incrementally as pages get built — see docs/workflow.md.
// RESET_PASSWORD is still path-only (ForgotPassword isn't built). DASHBOARD
// is built from a requested Figma screenshot — no dashboard API/slice
// exists yet, so its figures are hardcoded (see pages/Dashboard/Dashboard.tsx).
// MEMBERS is a showcase page for the Table/CustomHeader toolkit — dummy
// data only, no `services/api/members.ts` or Redux slice exists yet.
// WAITLIST_APPLICATION_REVIEW is reached by clicking a Waitlist row — no
// `sidebar` entry (it's a detail page, not primary nav) and no real
// application-detail endpoint yet, so its data is hardcoded (see
// pages/ApplicationReview/ApplicationReview.tsx). `:memberId` follows the
// same dynamic-route naming `lib/pageTitle.ts` already looks for (a
// trailing "Id" segment).
// MEMBER_DETAILS is reached by clicking a Members row — same
// no-sidebar-entry/dynamic-`:memberId` convention as
// WAITLIST_APPLICATION_REVIEW above (a different route, same param name;
// no collision since React Router scopes params per route). Built from a
// requested Figma screenshot: the member identity/contact fields are real
// (matched from the same `GET /users` Members.tsx already loads, by id —
// no confirmed single-member endpoint exists), but membership plan/
// billing and gathering-attendance history have no real endpoint at all
// yet, so those stay hardcoded (see pages/MemberDetails/MemberDetails.tsx).
// QUESTIONNAIRES is a new "Membership" nav section entry alongside
// Members/Waitlist — real data now: `GET/POST/PATCH/DELETE /questions`
// (store/slices/QuestionSlice.ts, services/api/questionnaire.ts), ported
// from the old project's Questionnaire-Management page (see
// docs/decisions.md). Categories (the form's Category dropdown) are
// read-only — `GET /question-categories` via CategorySlice — there's no
// category management page in the old project either.
// GATHERINGS is built from a requested Figma screenshot, same as Dashboard —
// no gatherings API/slice exists yet, so its cards/stats/past-gatherings
// table are all hardcoded (see pages/Gatherings/Gatherings.tsx). Its
// `section: "Programming"` matches the section already reserved in
// `lib/sidebarNavigation.ts`'s `SIDEBAR_SECTION_ORDER`.
// GATHERINGS_CREATE is reached via Gatherings' "Create Gathering" button —
// no `sidebar` entry (a detail/creation flow, not primary nav), no real
// create-gathering endpoint yet, so its option catalogs (dates, times,
// hosts, ...) are hardcoded (see pages/CreateGathering/CreateGathering.tsx).
// GATHERINGS_EDIT is reached via a GatheringCard's "Manage" button — same
// CreateGathering component as GATHERINGS_CREATE, in edit mode. `:eventId`
// is the trailing segment (same convention `lib/pageTitle.ts` already
// looks for, per WAITLIST_APPLICATION_REVIEW's `:memberId` above).
// GATHERING_DETAILS is reached by clicking anywhere else on a
// GatheringCard — a real guest list: `GET /admin/events/{eventId}/
// participants` (services/api/events.ts's `getEventParticipants`). No
// `sidebar` entry, same detail-page convention as MEMBER_DETAILS.
// TICKETING is built from a requested Figma screenshot, same convention as
// Dashboard/Gatherings — no ticketing/orders API or slice exists yet, so
// its stat cards/table are all hardcoded (see pages/Ticketing/Ticketing.tsx
// and pages/Ticketing/lib/mockOrders.ts). `section: "Programming"` — moved
// there from an original guess of "Revenue" once a real Figma sidebar
// (the one CITIES_CHAPTERS below was built from) showed Ticketing actually
// sits under Programming, alongside Gatherings and Cities & Chapters; see
// docs/decisions.md.
// CITIES_CHAPTERS is built from a requested Figma screenshot, same
// convention as Dashboard/Gatherings/Ticketing — no chapters/venues API or
// slice exists yet, so its chapter cards and venues table are all
// hardcoded (see pages/CitiesChapters/CitiesChapters.tsx and
// pages/CitiesChapters/lib/mockChapters.ts). `section: "Programming"`
// matches the section already reserved in `lib/sidebarNavigation.ts`'s
// `SIDEBAR_SECTION_ORDER`.
// SUBSCRIPTIONS is a new "Revenue" nav section (first page under it —
// Payments isn't built yet), same dummy-data convention, figures matched
// to DASHBOARD's for consistency (see pages/Subscriptions/Subscriptions.tsx),
// using PlaceholderNavIcon pending a dedicated icon.
// PAYMENTS is the second "Revenue" nav entry, built from a requested
// screenshot — but unlike most screenshot-built pages, its admin list is
// real: `GET /admin/payments` (`paymentSlice`/`services/api/payments.ts`).
// The screenshot itself was a subscription/billing summary design (room,
// started, renews, auto-renew) that this endpoint doesn't back at all —
// it's a flat payment-transaction log — so per the user those four
// columns plus both stat cards and the status filter pills stay
// decorative (see pages/Payments/Payments.tsx and docs/decisions.md #89),
// while Member/Amount/Payment type/Status render real data.
// SUPPORT is a new "System" nav section entry (help-content management) —
// no support API/slice exists yet, so it's dummy data only (see
// pages/Support/Support.tsx), same mock-data convention as QUESTIONNAIRES.
// Uses PlaceholderNavIcon pending a dedicated icon. NB its `section` below is
// actually "Engagement", not "System" as this comment long claimed — a
// pre-existing mismatch, not something this change fixes (see docs/decisions.md #68).
// TEAM_ROLES is a new "System" nav section entry, built from a requested
// screenshot the same way Dashboard/Gatherings/Ticketing/CitiesChapters
// were — no admin/role API or Redux slice exists yet, so its admin list
// and permissions matrix are hardcoded (see
// pages/TeamRoles/lib/mockTeamData.ts and docs/decisions.md #77).
// AUDIT_LOG is the first real "System" nav section entry — built from a
// requested screenshot, same convention as Dashboard/Members, but with a
// real API this time: `GET /admin/action-logs` via `auditLogSlice`/
// `services/api/auditLog.ts` (endpoint and response shape given directly
// by the user; request params are a guess — see decisions.md #68). Its
// 5 stat cards and two of the screenshot's four filters ("Admin"/"Result")
// have no backing field in that response at all, so they're either
// hardcoded (stat cards, matching Members'/Dashboard's precedent) or
// dropped rather than faked (see docs/decisions.md #68).
const routes: {
  routes: Record<string, RouteConfig>;
  publicRoutes: PublicRoutes;
  privateRoutes: PrivateRoutes;
} = {
  routes: {},
  publicRoutes: {
    LOGIN: {
      path: "/login",
      element: <Login />,
    },
    RESET_PASSWORD: {
      path: "/reset-password",
    },
  },
  privateRoutes: {
    DASHBOARD: {
      path: "/dashboard",
      element: <Dashboard />,
      pageName: captalizeEachWord("dashboard overview"),
      sidebar: { show: true, icon: GridIcon, section: "Overview", name: captalizeEachWord("dashboard") },
      permissionName: "getDashboard",
    },
    WAITLIST: {
      path: "/waitlist",
      element: <Waitlist />,
      pageName: captalizeEachWord("waitlist & applications"),
      sidebar: { show: true, icon: WaitListIcon, section: "Membership", name: captalizeEachWord("waitlist") },
    },
    WAITLIST_APPLICATION_REVIEW: {
      path: "/waitlist/:memberId",
      element: <ApplicationReview />,
      pageName: captalizeEachWord("application review"),
    },
    MEMBERS: {
      path: "/members",
      element: <Members />,
      pageName: captalizeEachWord("member management"),
      sidebar: { show: true, icon: UsersIcon, section: "Membership", name: captalizeEachWord("members") },
    },
    MEMBER_DETAILS: {
      path: "/members/:memberId",
      element: <MemberDetails />,
      pageName: captalizeEachWord("member"),
    },
    QUESTIONNAIRES: {
      path: "/questionnaires",
      element: <QuestionnaireManagement />,
      pageName: captalizeEachWord("questionnaires"),
      sidebar: { show: true, icon: QuestionnaireIcon, section: "Membership", name: captalizeEachWord("questionnaires") },
    },
    GATHERINGS: {
      path: "/gatherings",
      element: <Gatherings />,
      pageName: captalizeEachWord("gatherings"),
      sidebar: { show: true, icon: CalendarIcon, section: "Programming", name: captalizeEachWord("gatherings") },
    },
    GATHERINGS_CREATE: {
      path: "/gatherings/new",
      element: <CreateGathering />,
      pageName: captalizeEachWord("create gathering"),
    },
    GATHERINGS_EDIT: {
      path: "/gatherings/edit/:eventId",
      element: <CreateGathering />,
      pageName: captalizeEachWord("edit gathering"),
    },
    GATHERING_DETAILS: {
      path: "/gatherings/:eventId",
      element: <GatheringDetails />,
      pageName: captalizeEachWord("gathering"),
    },
    TICKETING: {
      path: "/ticketing",
      element: <Ticketing />,
      pageName: captalizeEachWord("ticketing & orders"),
      sidebar: { show: true, icon: TicketIcon, section: "Programming", name: captalizeEachWord("ticketing") },
    },
    // DOOR_CHECK_IN scans a member's ticket QR with the phone camera
    // (pages/DoorCheckIn). `mobileOnly` keeps it out of the desktop
    // sidebar; opened on desktop anyway, the page shows a notice instead.
    DOOR_CHECK_IN: {
      path: "/door-check-in",
      element: <DoorCheckIn />,
      pageName: captalizeEachWord("door check-in"),
      sidebar: {
        show: true,
        icon: QrScanIcon,
        section: "Programming",
        name: captalizeEachWord("door check-in"),
        mobileOnly: true,
      },
    },
    CITIES_CHAPTERS: {
      path: "/cities-chapters",
      element: <CitiesChapters />,
      pageName: captalizeEachWord("cities & chapters"),
      sidebar: {
        show: true,
        icon: LocationPinIcon,
        section: "Programming",
        name: captalizeEachWord("cities & chapters"),
      },
    },
    SUBSCRIPTIONS: {
      path: "/subscriptions",
      element: <Subscriptions />,
      pageName: captalizeEachWord("subscriptions"),
      sidebar: { show: true, icon: SubcriptionIcon, section: "Revenue", name: captalizeEachWord("subscriptions") },
    },
    PAYMENTS: {
      path: "/payments",
      element: <Payments />,
      pageName: captalizeEachWord("payments"),
      sidebar: { show: true, icon: PaymentIcon, section: "Revenue", name: captalizeEachWord("payments") },
    },
    PAST_EVENTS: {
      path: "/past-events",
      element: <PastEvents />,
      pageName: captalizeEachWord("past events"),
      sidebar: {
        show: true,
        icon: PastEventIcon,
        section: "Engagement",
        name: captalizeEachWord("past events"),
      },
    },
    SUPPORT: {
      path: "/support",
      element: <Support />,
      pageName: captalizeEachWord("support"),
      sidebar: { show: true, icon: PastEventIcon, section: "Engagement", name: captalizeEachWord("support") },
    },
    TEAM_ROLES: {
      path: "/team-roles",
      element: <TeamRoles />,
      pageName: captalizeEachWord("team & roles"),
      sidebar: { show: true, icon: TeamsIcon, section: "System", name: captalizeEachWord("team & roles") },
    },
    AUDIT_LOG: {
      path: "/audit-log",
      element: <AuditLog />,
      pageName: captalizeEachWord("audit log"),
      sidebar: { show: true, icon: AuditLogIcon, section: "System", name: captalizeEachWord("audit log") },
    },
    SETTINGS: {
      path: "/settings",
      element: <Settings />,
      pageName: captalizeEachWord("settings"),
      sidebar: { show: true, icon: SettingsIcon, section: "System", name: captalizeEachWord("settings") },
    },
  },
};

export default routes;
